<template>
  <div class="min-h-screen bg-[#121212] flex flex-col">
    <div class="flex-grow">
      <router-view></router-view>
      <MobileNav v-if="showMobileNav" />
      <ToastNotification />
      <PopupModal v-if="showSubscribeNudge" @popup-action="handlePopupAction" />
      <SubscribeBanner v-if="showSubscribeNudge" />
      <SubscribeInterstitial v-if="showSubscribeNudge" />
    </div>
  </div>
</template>

<script>
import MobileNav from './components/MobileNav.vue'
import ToastNotification from '@/components/Toast.vue'
import PopupModal from '@/components/PopupModal.vue'
import SubscribeBanner from '@/components/SubscribeBanner.vue'
import SubscribeInterstitial from '@/components/SubscribeInterstitial.vue'

export default {
  name: 'App',
  data() {
    return {
      scrollCheckInterval: null
    }
  },
  components: {
    MobileNav,
    ToastNotification,
    PopupModal,
    SubscribeBanner,
    SubscribeInterstitial
  },
  computed: {
    showMobileNav() {
      const hideOnRoutes = ['/login', '/register', '/mini-register'];
      return !hideOnRoutes.includes(this.$route.path);
    },
    showSubscribeNudge() {
      // Hide on login, registration, and plans pages
      const hideOnRoutes = ['/login', '/register', '/mini-register', '/plans'];
      if (hideOnRoutes.includes(this.$route.path)) return false;
      // Hide for subscribed users
      const user = this.$store.getters.currentUser;
      if (user && user.is_subscribed && new Date(user.subscription_end_date) > new Date()) return false;
      if (this.$store.getters.hasActiveSubscription) return false;
      return true;
    }
  },
  watch: {
    // Watch for route changes to ensure scroll is restored
    '$route'() {
      // Ensure scroll is enabled when route changes
      this.$nextTick(() => {
        this.ensurePageScrollability();
      });
    },
    // Watch for authentication status changes
    '$store.getters.isAuthenticated'(newVal) {
      if (newVal) {
        console.log('User authenticated, triggering subscription check');
        this.checkSubscriptionStatus();
      }
    }
  },
  mounted() {
    console.log('App mounted, checking authentication status');
    
    // Ensure page scrolling is enabled on app mount
    this.ensurePageScrollability();
    
    // Check if user is authenticated
    const isAuthenticated = this.$store.getters.isAuthenticated;
    const user = this.$store.getters.currentUser;
    
    console.log('Authentication status:', { isAuthenticated, user: user ? 'exists' : 'null' });
    
    // Check and clean expired orders on page load
    this.checkAndCleanExpiredOrders();
    
    // Only check notifications and payments if user is authenticated
    if (isAuthenticated && user) {
    // Check for success messages
    this.checkAndShowToast('registrationSuccess', 'registrationMessage');
    this.checkAndShowToast('loginSuccess', 'loginMessage');
      // Check for pending payments on mount
      this.checkPendingPayments();
      // Check for user notifications (like account deletion)
      this.checkUserNotifications();
      
      // Set up intervals for background checking
      // Check pending payments every 30 seconds
      this.paymentCheckInterval = setInterval(() => {
        this.checkPendingPayments();
      }, 30000);
      
      // Check subscription status every 2 minutes
      this.subscriptionCheckInterval = setInterval(() => {
        this.checkSubscriptionStatus();
      }, 120000);
      
      // Check user status every 30 seconds (Auto-Logout)
      this.statusCheckInterval = setInterval(() => {
        this.checkUserStatus();
      }, 30000);
      
      // Listen for app resume/focus
      window.addEventListener('focus', this.checkPendingPayments);
      window.addEventListener('focus', this.checkUserNotifications);
      window.addEventListener('focus', this.checkAndCleanExpiredOrders);
    } else {
      console.log('User not authenticated, skipping background checks');
    }
    
    // Add global event listeners for scroll restoration
    window.addEventListener('beforeunload', this.ensurePageScrollability);
    window.addEventListener('pagehide', this.ensurePageScrollability);
    
    // Add periodic scroll check (every 5 seconds)
    this.scrollCheckInterval = setInterval(() => {
      if (document.body.style.overflow === 'hidden') {
        console.log('⚠️ Scroll blocked detected, restoring...');
        this.ensurePageScrollability();
      }
    }, 5000);
  },
  beforeUnmount() {
    // Clear all intervals
    if (this.paymentCheckInterval) {
      clearInterval(this.paymentCheckInterval);
    }
    if (this.subscriptionCheckInterval) {
      clearInterval(this.subscriptionCheckInterval);
    }
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
    }
    
    // Clear event listeners
    window.removeEventListener('focus', this.checkPendingPayments);
    window.removeEventListener('focus', this.checkUserNotifications);
    window.removeEventListener('focus', this.checkAndCleanExpiredOrders);
    window.removeEventListener('beforeunload', this.ensurePageScrollability);
    window.removeEventListener('pagehide', this.ensurePageScrollability);
    
    // Clear the scroll check interval
    if (this.scrollCheckInterval) {
      clearInterval(this.scrollCheckInterval);
    }
  },
  methods: {
    checkAndShowToast(successKey, messageKey) {
      const isSuccess = localStorage.getItem(successKey);
      if (isSuccess === 'true') {
        // Show success toast
        try {
          if (this.$toast) {
            this.$toast.success(localStorage.getItem(messageKey) || 'Operation completed successfully!', {
              position: 'top-right',
              duration: 3000
            });
          }
        } catch (error) {
          console.warn('Toast notification failed:', error);
        }
        // Clear the success message
        localStorage.removeItem(successKey);
        localStorage.removeItem(messageKey);
      }
    },
    async checkPendingPayments() {
      const pendingPayment = localStorage.getItem('pending_payment');
      if (pendingPayment) {
        try {
          const paymentData = JSON.parse(pendingPayment);
          console.log('=== CHECKING PENDING PAYMENT ===');
          console.log('Payment data:', paymentData);
          
          // 15 నిమిషాల లోపల payment అయితే మాత్రమే
          const timeDiff = Date.now() - paymentData.timestamp;
          console.log('Time difference:', timeDiff, 'ms');
          
          if (timeDiff < 15 * 60 * 1000) {
            console.log('Payment is within time limit, proceeding with verification');
            
            // Retry count check
            if (paymentData.retry_count >= 10) {
              console.log('Max retries reached, removing pending payment');
              localStorage.removeItem('pending_payment');
              return;
            }
            
            console.log('Attempting payment verification...');
            // Backend కి verify-payment API call చేయండి
            const verifyResponse = await this.$axios.post('/users/verify-payment/', {
              razorpay_payment_id: paymentData.payment_id,
              razorpay_order_id: paymentData.order_id,
              razorpay_signature: paymentData.signature,
              plan_id: paymentData.plan_id,
              phone: paymentData.phone || this.$store.getters.currentUser?.phone
            });
            
            console.log('Verification response:', verifyResponse.data);
            
            if (verifyResponse.data.status === 'success') {
              localStorage.removeItem('pending_payment');
              this.$store.commit('setSubscription', verifyResponse.data.subscription);
              this.$store.dispatch('showToast', {
                message: 'Payment successful! Your subscription is now active.',
                type: 'success'
              });
              console.log('Pending payment verified successfully');
            } else {
              console.log('Payment verification failed:', verifyResponse.data);
              // Increment retry count
              paymentData.retry_count = (paymentData.retry_count || 0) + 1;
              localStorage.setItem('pending_payment', JSON.stringify(paymentData));
              console.log('Updated retry count:', paymentData.retry_count);
            }
          } else {
            // Payment చాలా పాతది అయితే, localStorage నుండి తీసేయండి
            localStorage.removeItem('pending_payment');
            console.log('Pending payment expired, removed from localStorage');
          }
        } catch (error) {
          console.error('Error checking pending payment:', error);
          // Increment retry count on error
          try {
            const paymentData = JSON.parse(pendingPayment);
            paymentData.retry_count = (paymentData.retry_count || 0) + 1;
            localStorage.setItem('pending_payment', JSON.stringify(paymentData));
            console.log('Error occurred, updated retry count:', paymentData.retry_count);
          } catch (parseError) {
            console.error('Error updating retry count:', parseError);
          }
        }
      } else {
        console.log('No pending payment found in localStorage');
      }
      
      // Also check subscription status directly
      await this.checkSubscriptionStatus();
    },
    
    // Check and clean expired orders
    checkAndCleanExpiredOrders() {
      try {
        const paymentOrder = localStorage.getItem('payment_order');
        if (paymentOrder) {
          const orderData = JSON.parse(paymentOrder);
          const orderTime = orderData.created_at;
          const currentTime = Date.now();
          const timeDiff = currentTime - orderTime;
          
          // If order is older than 30 minutes, remove it
          if (timeDiff > 30 * 60 * 1000) { // 30 minutes
            localStorage.removeItem('payment_order');
            console.log('Expired payment order removed from localStorage');
          } else {
            console.log('Valid payment order found, keeping it');
          }
        } else {
          console.log('No payment order found in localStorage');
        }
      } catch (error) {
        console.error('Error checking expired orders:', error);
        // Remove corrupted data
        localStorage.removeItem('payment_order');
      }
    },
    
    async checkSubscriptionStatus() {
      try {
        const user = this.$store.getters.currentUser;
        if (!user || !user.phone) {
          console.log('No user found, skipping subscription check');
          return;
        }
        
        console.log('=== CHECKING SUBSCRIPTION STATUS ===');
        console.log('User phone:', user.phone);
        
        const response = await this.$axios.get('/users/check-subscription-status/', {
          params: { phone: user.phone }
        });
        
        console.log('Subscription status response:', response.data);
        
        if (response.data.status === 'success') {
          if (response.data.is_subscribed) {
            console.log('User has active subscription:', response.data.subscription);
            
            // Update store with subscription data
            this.$store.commit('setSubscription', response.data.subscription);
            
            // Sync user object with subscription status
            const currentUser = this.$store.getters.currentUser;
            if (currentUser && !currentUser.is_subscribed) {
              const updatedUser = { ...currentUser, is_subscribed: true, subscription_end_date: response.data.subscription.end_date || response.data.subscription.subscription_end_date };
              this.$store.commit('setUser', updatedUser);
            }
            
            // Only show toast and clear pending payment if there was a pending payment
            // (i.e., subscription just got activated after payment, not on every routine check)
            const hadPendingPayment = localStorage.getItem('pending_payment');
            if (hadPendingPayment) {
              localStorage.removeItem('pending_payment');
              console.log('Subscription activated after pending payment, showing toast');
              this.$store.dispatch('showToast', {
                message: 'Your subscription is active!',
                type: 'success'
              });
            } else {
              console.log('Subscription active (routine check), no toast needed');
            }
          } else {
            console.log('User has no active subscription');
            // Sync user object - mark as not subscribed
            this.$store.commit('setSubscription', null);
            const currentUser = this.$store.getters.currentUser;
            if (currentUser && currentUser.is_subscribed) {
              const updatedUser = { ...currentUser, is_subscribed: false };
              this.$store.commit('setUser', updatedUser);
            }
            // Check if there's a pending payment
            const pendingPayment = localStorage.getItem('pending_payment');
            if (pendingPayment) {
              console.log('Found pending payment, will retry verification');
            }
          }
        } else {
          console.log('Subscription status check failed:', response.data);
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        console.error('Error details:', error.response?.data);
      }
    },
    async checkUserNotifications() {
      try {
        // Check if user is authenticated
        const isAuthenticated = this.$store.getters.isAuthenticated;
        const user = this.$store.getters.currentUser;
        
        if (!isAuthenticated || !user || !user.phone) {
          console.log('User not authenticated, skipping notification check');
          return;
        }

        console.log('Checking notifications for user:', user.phone);

        const response = await this.$axios.get(`/users/check-notifications/?phone=${user.phone}`);
        
        if (response.data.status === 'success') {
          // Check if user still exists in database
          if (response.data.user_exists === false) {
            console.log('User no longer exists in database, logging out');
            
            // Complete logout process
            this.performCompleteLogout('Your account has been deleted. You have been logged out.');
            return;
          }

          // Check for notifications
          if (response.data.has_notifications) {
            console.log('Found notifications:', response.data.notifications);
            
            // Check for account deletion notification
            const accountDeletedNotification = response.data.notifications.find(
              notification => notification.type === 'account_deleted'
            );

            if (accountDeletedNotification) {
              console.log('Account deletion notification found, logging out user');
              
              // Complete logout process
              this.performCompleteLogout('Your account has been deleted by admin. You have been logged out.');
            }
          } else {
            console.log('No notifications found');
          }
        }
      } catch (error) {
        console.error('Error checking user notifications:', error);
      }
    },
    async checkUserStatus() {
      try {
        const isAuthenticated = this.$store.getters.isAuthenticated;
        if (!isAuthenticated) return;
        
        // JWT token in Authorization header handles authentication.
        // Returns 200 if active, 403 if blocked, 401 if token expired.
        // api.js interceptor handles 401/403 → auto-logout.
        await this.$axios.get('/users/check-status/');
      } catch (error) {
        // 401/403 is handled by api.js interceptor, other errors silently ignored
      }
    },
    performCompleteLogout(message) {
      console.log('Performing complete logout');
      
      // Clear all localStorage and sessionStorage
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear specific items to be sure
      const itemsToRemove = [
        'user', 'isAuthenticated', 'token', 'subscription', 'csrfToken',
        'pending_payment', 'registrationSuccess', 'registrationMessage',
        'loginSuccess', 'loginMessage', 'hasCompletedRegistration', 'app_version'
      ];
      
      itemsToRemove.forEach(item => {
        localStorage.removeItem(item);
        sessionStorage.removeItem(item);
      });
      
      // Clear store
      this.$store.commit('logout');
      
      // Clear all cookies
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      
      // Clear axios default headers
      if (this.$axios && this.$axios.defaults) {
        this.$axios.defaults.headers.common['Authorization'] = '';
        this.$axios.defaults.headers.common['X-CSRFToken'] = '';
      }
      
      // Show notification
      this.$store.dispatch('showToast', {
        message: message || 'You have been logged out.',
        type: 'error',
        duration: 5000
      });

      // Redirect to login page
      this.$router.push('/login');
      
      console.log('Complete logout finished');
    },
    handlePopupAction() {
      // Handle popup action - you can add custom logic here
      console.log('Popup action handled');
    },
    
    // Ensure page scrolling is enabled
    ensurePageScrollability() {
      console.log('🔧 Ensuring page scrollability...');
      
      // Reset body styles
      document.body.style.overflow = 'auto';
      document.body.style.position = 'static';
      document.body.style.height = 'auto';
      document.body.style.width = 'auto';
      document.body.style.transform = 'none';
      document.body.style.willChange = 'auto';
      document.body.style.touchAction = 'auto';
      document.body.style.pointerEvents = 'auto';
      document.body.style.userSelect = 'auto';
      document.body.style.webkitUserSelect = 'auto';
      document.body.style.webkitOverflowScrolling = 'touch';
      
      // Reset html styles
      document.documentElement.style.overflow = 'auto';
      document.documentElement.style.height = 'auto';
      document.documentElement.style.position = 'static';
      document.documentElement.style.touchAction = 'auto';
      
      // Remove any video-related classes
      document.body.classList.remove('vjs-fullscreen', 'video-playing', 'no-scroll', 'force-scrollable');
      document.documentElement.classList.remove('vjs-fullscreen', 'video-playing', 'no-scroll');
      
      // Clear any inline styles that might block scroll
      document.body.style.cssText = document.body.style.cssText.replace(/overflow:\s*[^;]+;?/g, 'overflow: auto;');
      document.documentElement.style.cssText = document.documentElement.style.cssText.replace(/overflow:\s*[^;]+;?/g, 'overflow: auto;');
      
      // Force a reflow to ensure styles are applied
      void document.body.offsetHeight;
      void document.documentElement.offsetHeight;
      
      // Test scroll functionality
      window.scrollTo(0, 1);
      window.scrollTo(0, 0);
      
      console.log('✅ Page scrollability ensured');
    }
  }
}
</script>

<style>
body {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: #121212;
  color: white;
  overflow-x: hidden;
  overflow-y: auto;
}

html {
  overflow-x: hidden;
  overflow-y: auto;
}

#app {
  min-height: 100vh;
  overflow-x: hidden;
}

/* Ensure scrolling works properly */
.min-h-screen {
  overflow-x: hidden;
  overflow-y: auto;
}

/* Force scrollable class for emergency use */
.force-scrollable {
  overflow: auto !important;
  position: static !important;
  height: auto !important;
  width: auto !important;
  transform: none !important;
  will-change: auto !important;
  touch-action: auto !important;
  pointer-events: auto !important;
}
</style>
